import { useEffect, useId, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { uk } from "date-fns/locale";
import { AlertTriangle, Ban, Loader2, X } from "lucide-react";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { fetchInteractionDetail, type InteractionDetail } from "@/api/interactions";
import { ApiClientError } from "@/api/client";

/**
 * D-151 S2 — confirm-крок «Заповнити звіт за менеджера» (visual-spec P62,
 * frontend/d151_s2_confirm_visual_v1.html — 4 стани):
 *   1 звичайний past/unsubmitted — reason + submit;
 *   2 не позначена «проведено» — + чекбокс «Підтверджую, що відбулася»;
 *   3 стара чернетка — warning (автор/вік) + чекбокс «Перезаписати»;
 *   4 свіжа чернетка — hard-block, CTA disabled.
 * Стан повністю server-derived (allowedActions + onBehalfContext) — FE нічого
 * не вгадує. Primary CTA disabled, доки reason ≥10 симв. ∧ всі потрібні чекбокси.
 */

export interface OnBehalfProceedContext {
  reason: string;
  confirmOccurredOnBehalf: boolean;
  overrideStaleDraft: boolean;
  expectedAssignedUserId: string;
  interactionRevision: number;
  managerName: string;
}

export function OnBehalfConfirmModal({
  interactionId,
  onClose,
  onProceed,
}: {
  interactionId: string | null;
  onClose: () => void;
  /** «Продовжити до звіту →» — далі незмінений C+-візард у supervisor-режимі. */
  onProceed: (d: InteractionDetail, ctx: OnBehalfProceedContext) => void;
}) {
  const open = interactionId !== null;
  const panelRef = useDrawerA11y(open, onClose);
  const headingId = useId();

  const [reason, setReason] = useState("");
  const [occChecked, setOccChecked] = useState(false);
  const [ovrChecked, setOvrChecked] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason("");
      setOccChecked(false);
      setOvrChecked(false);
    }
  }, [open]);

  const q = useQuery({
    queryKey: ["onBehalfConfirm", interactionId],
    queryFn: ({ signal }) => fetchInteractionDetail(interactionId as string, signal),
    enabled: open,
    staleTime: 0, // server-derived стан + свіжий CAS-токен — завжди свіже
    retry: (count, e) => !(e instanceof ApiClientError && e.status === 403) && count < 2,
  });

  if (!open) return null;

  const d = q.data;
  const ctx = d?.onBehalfContext ?? null;
  const blocked = !!d?.allowedActions.canFillReportOnBehalfBlockedByFreshDraft;
  const allowed = !!d?.allowedActions.canFillReportOnBehalf;

  const fmt = (v: string | null | undefined, pattern = "dd.MM HH:mm") => {
    if (!v) return null;
    try {
      return format(parseISO(v.replace(" ", "T")), pattern, { locale: uk });
    } catch {
      return null;
    }
  };

  const needOccurred = !!ctx?.occurredConfirmRequired;
  const staleDraft = !!ctx && ctx.draft.hasContent && !ctx.draft.fresh && !blocked;
  const reasonOk = reason.trim().length >= 10 && reason.trim().length <= 500;
  const canProceed =
    allowed && !blocked && reasonOk
    && (!needOccurred || occChecked)
    && (!staleDraft || ovrChecked);

  const ageLabel = (h: number | null) => {
    if (h === null) return "раніше";
    if (h < 1) return "менш як годину тому";
    if (h < 48) return `${h} год тому`;
    return `${Math.floor(h / 24)} дн. тому`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} aria-hidden>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[520px] rounded-xl border bg-background p-5 shadow-2xl outline-none"
      >
        <div className="mb-1 flex items-start justify-between gap-2">
          <h3 id={headingId} className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            Звіт від імені менеджера
            {ctx && (ctx.overdue ? (
              <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[10.5px] font-bold text-destructive">
                Прострочений з {fmt(ctx.overdueSince, "dd.MM HH:mm")}
              </span>
            ) : (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
                Звіт ще не подано
              </span>
            ))}
          </h3>
          <button type="button" onClick={onClose} aria-label="Закрити" className="rounded-md p-1 text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        {q.isPending ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Завантаження…
          </div>
        ) : !d ? (
          <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            Не вдалося завантажити зустріч.
          </p>
        ) : (
          <>
            <p className="mb-3 text-xs text-muted-foreground">
              Зустріч: {d.summary}
              {d.accountName ? ` · ${d.accountName}` : ""}
              {d.occurredAt ? ` · ${fmt(d.occurredAt, "dd.MM.yyyy")}` : ""}
              {" · Менеджер: "}
              <b className="text-foreground">{d.assignedUserName ?? "—"}</b>
            </p>

            {blocked ? (
              /* стан 4 — свіжа чернетка: hard-block, textarea відсутня */
              <div className="mb-3 flex items-start gap-2 rounded-md bg-destructive/5 px-3 py-2.5 text-xs text-destructive">
                <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>
                  <b>Менеджер зараз працює над звітом</b> — чернетку збережено{" "}
                  {ageLabel(ctx?.draft.ageHours ?? null)}. Заповнення від імені недоступне,
                  доки чернетка свіжа (48 год). Зв'яжіться з менеджером або поверніться пізніше.
                </span>
              </div>
            ) : !allowed ? (
              <div className="mb-3 rounded-md bg-muted px-3 py-2.5 text-xs text-muted-foreground">
                Дія недоступна — стан зустрічі змінився. Оновіть список.
              </div>
            ) : (
              <>
                <div className="mb-3 rounded-md bg-muted/60 px-3 py-2.5 text-xs">
                  Ви заповнюєте звіт <b>від імені {d.assignedUserName}</b>. Позначка
                  «заповнив керівник» і причина будуть видимі менеджеру та в Контролі
                  команди. Змінити це після подачі неможливо.
                </div>

                {staleDraft && (
                  <div className="mb-3 flex items-start gap-2 rounded-md border border-warning-foreground/20 bg-warning px-3 py-2.5 text-xs text-warning-foreground">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                    <span>
                      Менеджер зберігав <b>чернетку звіту {ageLabel(ctx?.draft.ageHours ?? null)}</b>
                      {ctx?.draft.savedAt ? ` (${fmt(ctx.draft.savedAt)})` : ""}. Якщо продовжите —
                      його чернетку буде перезаписано; повна копія збережеться в аудиті
                      й буде доступна менеджеру.
                    </span>
                  </div>
                )}

                {needOccurred && (
                  <div className="mb-3 rounded-md border border-warning-foreground/20 bg-warning px-3 py-2.5 text-xs text-warning-foreground">
                    Зустріч <b>не позначена як проведена</b>. Подання звіту зафіксує, що вона відбулася.
                  </div>
                )}

                <label className="mb-1 block text-xs font-semibold">
                  Причина, чому звіт заповнює керівник <span className="text-destructive">*</span>
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="Мін. 10 символів, до 500. Лише текст."
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
                <p className="mb-2 text-right text-[11px] tabular-nums text-muted-foreground">
                  {reason.trim().length} / 500
                </p>

                {needOccurred && (
                  <label className="mb-2 flex items-start gap-2 text-xs">
                    <input type="checkbox" className="mt-0.5" checked={occChecked} onChange={(e) => setOccChecked(e.target.checked)} />
                    <span><b>Підтверджую, що зустріч фактично відбулася</b> (буде записано окремою подією аудиту)</span>
                  </label>
                )}
                {staleDraft && (
                  <label className="mb-2 flex items-start gap-2 text-xs">
                    <input type="checkbox" className="mt-0.5" checked={ovrChecked} onChange={(e) => setOvrChecked(e.target.checked)} />
                    <span><b>Перезаписати чернетку менеджера</b> (копія піде в аудит)</span>
                  </label>
                )}
              </>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="rounded-md border border-border px-3 py-2 text-sm font-semibold hover:bg-muted">
                {blocked ? "Закрити" : "Скасувати"}
              </button>
              <button
                type="button"
                disabled={!canProceed}
                onClick={() =>
                  onProceed(d, {
                    reason: reason.trim(),
                    confirmOccurredOnBehalf: needOccurred,
                    overrideStaleDraft: staleDraft,
                    expectedAssignedUserId: ctx?.expectedAssignedUserId ?? "",
                    interactionRevision: d.interactionRevision,
                    managerName: d.assignedUserName ?? "",
                  })
                }
                className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-45"
              >
                Продовжити до звіту →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
